import { useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Users, Wallet, Banknote, Clock, Hourglass, TrendingUp, Gift, HandCoins, Youtube, Layers, PlayCircle, Loader2, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd, fmtDate } from "@/lib/api";
import { useFetch, PageHeader, StatCard, Panel, Loading } from "@/components/common";
import { ChartTip } from "@/pages/Dashboard";

export const waitInfo = (ts) => {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 60000));
  const label = mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return { label, overdue: mins >= 240 };
};

const PendingRow = ({ p }) => {
  const w = waitInfo(p.submitted_at);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
      <span className="num text-[#10B981]">{p.user_code}</span>
      <span className="num text-white">{usd(p.amount)}</span>
      <span className="text-slate-400">{p.method_label}</span>
      <span className="num text-xs text-slate-400">{p.txn_id}</span>
      <span className="text-xs text-slate-500">{fmtDate(p.submitted_at)}</span>
      <span className={`num rounded-full px-2 py-0.5 text-xs ${w.overdue ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"}`}>{w.overdue ? "Overdue " : "Waiting "}{w.label}</span>
    </div>
  );
};

export default function AdminOverview() {
  const { data: d, loading, reload } = useFetch("/admin/overview");
  const [busy, setBusy] = useState(false);
  if (loading || !d) return <Loading />;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/run-daily-returns");
      toast.success(data.skipped ? "Daily returns are disabled in settings" : `Paid ${data.paid} investments · ${usd(data.total)}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const lr = d.last_return_run;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Admin Control Panel" title="Platform overview"
        actions={<button className="nx-btn-primary" onClick={run} disabled={busy} data-testid="run-daily-returns-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />}Run today's returns</button>} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Users" value={d.total_users} icon={Users} testid="admin-stat-users" />
        <StatCard label="Approved deposits" value={usd(d.total_deposits)} icon={Wallet} accent testid="admin-stat-deposits" />
        <StatCard label="Paid withdrawals" value={usd(d.total_withdrawals)} icon={Banknote} testid="admin-stat-withdrawals" />
        <StatCard label="User balances" value={usd(d.user_balances)} icon={Layers} testid="admin-stat-balances" sub={`+ ${usd(d.bonus_balances)} locked bonus`} />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Link to="/admin/deposits"><StatCard label="Pending deposits" value={d.pending_deposits_count} icon={Clock} testid="admin-stat-pending-deposits" sub={usd(d.pending_deposits_amount)} /></Link>
        <Link to="/admin/withdrawals"><StatCard label="Pending withdrawals" value={d.pending_withdrawals_count} icon={Hourglass} testid="admin-stat-pending-withdrawals" sub={usd(d.pending_withdrawals_amount)} /></Link>
        <Link to="/admin/commissions"><StatCard label="Pending commissions" value={d.pending_commissions_count} icon={HandCoins} testid="admin-stat-pending-commissions" sub={usd(d.pending_commissions_amount)} /></Link>
        <Link to="/admin/tickets"><StatCard label="Unresolved tickets" value={d.open_tickets} icon={LifeBuoy} testid="admin-stat-open-tickets" /></Link>
      </div>
      <Panel title="Deposits awaiting verification (oldest first · 4h target)" action={<Link to="/admin/deposits" className="text-xs text-[#10B981]" data-testid="overview-view-deposits">Review all</Link>}>
        <div className="divide-y divide-[#1E2738]" data-testid="overview-pending-deposits">
          {!d.pending_deposits_list.length && <p className="px-5 py-6 text-sm text-slate-500">No deposits waiting</p>}
          {d.pending_deposits_list.map((p) => <PendingRow key={p.id} p={p} />)}
        </div>
      </Panel>
      <div className="grid gap-6 lg:grid-cols-12">
        <Panel title="Approved deposits (last 14 days)" className="lg:col-span-8">
          {d.deposits_chart.length ? (
            <div className="h-64 p-4" data-testid="admin-deposits-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.deposits_chart}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} tickFormatter={(x) => x.slice(5)} />
                  <YAxis tickLine={false} axisLine={false} width={50} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: "#1A2232" }} />
                  <Bar dataKey="amount" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="py-24 text-center text-sm text-slate-500">No approved deposits yet</p>}
        </Panel>
        <div className="space-y-4 lg:col-span-4">
          <StatCard label="Active in AI plans" value={usd(d.active_investments)} icon={Layers} testid="admin-stat-active-inv" />
          <StatCard label="Returns paid" value={usd(d.returns_paid)} icon={TrendingUp} accent testid="admin-stat-returns" />
          <StatCard label="Welcome bonuses issued" value={usd(d.welcome_bonus_total)} icon={Gift} testid="admin-stat-welcome-bonus" />
          <div className="nx-card p-4 text-xs text-slate-400" data-testid="last-return-run">
            Last returns run: {lr ? <><span className="num text-white">{lr.date}</span> · {lr.paid} paid · {usd(lr.total)} · {fmtDate(lr.created_at)}</> : "never"}
            <p className="mt-1 text-slate-500">Automatic run daily at 00:05 UTC.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
